import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import { Bodytext, SelectionList } from '@acorns/web-components'
import styled from 'styled-components'

import Button from 'components/Button'
import FormContainer from 'components/FormContainer'
import Layout from 'components/Layout'
import { Body, Heading2 } from 'components/Typography'
import { style } from 'theme'

type OptionCardProps = {
  checked: boolean
  name: string
}

type SelectionListProps = {
  checked: boolean
  name: string
  onClick: () => void
  testId?: string
  title: string
  text: string
}

export const ActionWrapper = styled.div`
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    background-color: white;
    bottom: 0;
    left: 0;
    padding: 15px 0 0 0;
    position: fixed;
    width: 100%;
  }
`

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const FormRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  flex: 100%;
  font-size: 14px;
  justify-content: center;
  padding: 0;
`

export const Label = styled.label`
  margin-right: 25px;
`

export const OptionCard = styled.div<Partial<OptionCardProps>>`
  border-radius: 10px;
  cursor: pointer;
  display: block;
  height: 108px;
  margin: 0 0 20px 0;
  overflow: none;
  padding: 20px;
  transition-property: box-shadow;
  transition-duration: 0.5s;
  width: 450px;
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    height: 100%;
    width: 315px;
  }

  ${props =>
    props.checked ? 'box-shadow: 0 3px 15px 0 rgba(0,0,0,0.05);' : 'border: 1px solid #F5F5F5;'}
`

const Grid = styled.div`
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 0px;
  position: relative;
`

const GridLeft = styled.div`
  grid-row-start: 1;
  grid-column-start: 1;

  grid-row-end: 3;
  grid-column-end: 2;
  padding-top: 20px;
  width: 48px;
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
  }
`

const GridMiddle = styled.div`
  grid-row-start: 1;
  grid-column-start: 2;

  grid-row-end: 3;
  grid-column-end: 7;
  width: 312px;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    width: 177px;
  }
`

const GridRight = styled.div`
  grid-row-start: 1;
  grid-column-start: 7;
  grid-row-end: 3;
  grid-column-end: 8;
  overflow: none;
  width: 46px;
`

const Content = styled.div`
  max-width: 312px;
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    max-width: 177px;
  }
`

const OptionCardTitle = styled(Heading2)`
  line-height: 22px;
`

const OptionCardText = styled(Bodytext)`
  color: ${style(theme => theme.colors.stone)};
  margin-top: 2px;
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    width: 177px;
  }
`

export const CheckedInput = styled.div`
  position: absolute;
  right: 0;
  top: 55%;
  transform: translateY(-55%);
`

export const StyledFormContainer = styled(FormContainer)`
  border-radius: 10px;
  max-width: 100%;
  margin: 0 auto;

  &&& {
    width: 450px;
    padding-bottom: 80x;
  }

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    &&& {
      width: 315px;
      padding-bottom: 0;
    }
  }
`

export const StyledBody = styled(Body)`
  font-size: 14px;
  line-height: 22px;
  margin: 0 auto 30px;
  max-width: 340px;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-bottom: 50px;
  }
  text-align: center;
`

export const StyledButton = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 24px 10px;
  width: 220px;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    bottom: 15px;
    height: 44px;
    margin: 8px 20px 20px 20px;
    position: sticky;
    width: calc(100vw - 40px);
  }
`

export const StyledImage = styled.img`
  height: auto;
  margin: 80px auto 0;
  max-width: 275px;
  width: 100%;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-top: 0px;
  }
`

export const StyledLayout = styled(Layout)`
  padding: 10px 0px 14px 0px;
`

const StyledListIconSmile = styled(AcornsIcon)`
  position: absolute;
  left: -3px;
  top: -3px;
`

const StyledListIconManual = styled(AcornsIcon)`
  position: absolute;
  top: 0px;
  left: 0x;
`

export const StyledSelectionList = styled(SelectionList)`
  height: 22px;
  margin-top: 15px;
  line-height: 22px;
  width: 450px;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-top: 7px;
  }
`
export const StyledTitle = styled(Heading2)`
  font-size: 22px;
  line-height: 30px;
  margin: 35px auto 6px;
`

export const RoundUpSelectionList = ({
  checked,
  name,
  onClick,
  testId,
  title,
  text,
}: SelectionListProps) => {
  return (
    <OptionCard onClick={onClick} data-testid={testId} checked={checked}>
      <Grid>
        <GridLeft>
          {name === 'automatic' ? (
            <StyledListIconSmile icon={AcornsIcon.Icon.SmileGreen} width={34} />
          ) : (
            <StyledListIconManual icon={AcornsIcon.Icon.Invite} width={28} />
          )}
        </GridLeft>

        <GridMiddle>
          <Content>
            <OptionCardTitle>{title}</OptionCardTitle>
            <OptionCardText>{text}</OptionCardText>
          </Content>
        </GridMiddle>
        <GridRight>
          <CheckedInput>
            <AcornsIcon
              icon={checked ? AcornsIcon.Icon.CheckmarkGreen : AcornsIcon.Icon.HollowCircle}
              width={22}
            />
          </CheckedInput>
        </GridRight>
      </Grid>
    </OptionCard>
  )
}
